import { useCallback, useEffect, useMemo, useState } from 'react';
import { Linking, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { usePostHog } from 'posthog-js/react';
import { useTier } from '../../context/TierContext';
import { useAuth } from '../../context/AuthContext';
import { openUpdatePaymentCheckout } from '../../lib/paddle';
import logger from '../../utils/logger';

// D-029 — Non-dismissible past-due banner. Auto-hides reactively when
// TierContext flips status out of past_due (payment retried → active, or
// grace elapsed → cancelled). Amber for most of the 3-day grace; shifts to
// red in the final 24h (visual urgency without relying on countdown copy).
//
// D-030 — Update-billing CTA opens an inline Paddle.Checkout overlay against
// the stored past-due transaction id. Paddle retries the charge on success;
// the resumed subscription webhook clears the banner. Falls back to a
// support mailto when the transaction id is missing or Paddle.js fails.
const FINAL_WARNING_MS = 24 * 60 * 60 * 1000;

const SUPPORT_FALLBACK = 'mailto:support@doocars.com?subject=Update%20my%20billing';

const PastDueBanner = () => {
  const { subscriptionStatus, proExpiresAt, pastDueTransactionId } = useTier();
  const { user } = useAuth();
  const posthog = usePostHog();
  const [now, setNow] = useState(() => Date.now());

  const expiresMs = useMemo(
    () => (proExpiresAt ? new Date(proExpiresAt).getTime() : null),
    [proExpiresAt]
  );

  const isUrgent = expiresMs != null && expiresMs - now < FINAL_WARNING_MS;

  useEffect(() => {
    if (subscriptionStatus !== 'past_due') return undefined;
    const id = setInterval(() => setNow(Date.now()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [subscriptionStatus]);

  const handleUpdateBilling = useCallback(async () => {
    const fallback = () => {
      Linking.openURL(SUPPORT_FALLBACK).catch(() => {});
    };

    if (!pastDueTransactionId) {
      posthog?.capture('past_due_card_update_opened', {
        outcome: 'fallback_no_transaction_id',
      });
      fallback();
      return;
    }

    posthog?.capture('past_due_card_update_opened', {
      outcome: 'overlay',
      transaction_id: pastDueTransactionId,
    });

    try {
      await openUpdatePaymentCheckout({
        transactionId: pastDueTransactionId,
        email: user?.email,
        onSuccess: () => {
          posthog?.capture('past_due_card_update_succeeded', {
            transaction_id: pastDueTransactionId,
          });
        },
        onError: (err) => {
          posthog?.capture('past_due_card_update_failed', {
            transaction_id: pastDueTransactionId,
            error: err?.message || String(err),
          });
        },
      });
    } catch (err) {
      logger.warn('[PastDueBanner] Paddle overlay failed, falling back to support email', err);
      posthog?.capture('past_due_card_update_failed', {
        transaction_id: pastDueTransactionId,
        error: err?.message || String(err),
        outcome: 'fallback_paddle_load_failed',
      });
      fallback();
    }
  }, [pastDueTransactionId, user?.email, posthog]);

  if (subscriptionStatus !== 'past_due') return null;

  const palette = isUrgent ? RED_PALETTE : AMBER_PALETTE;

  return (
    <View
      style={[styles.banner, { backgroundColor: palette.bg, borderBottomColor: palette.border }]}
      accessibilityRole="alert"
    >
      <MaterialCommunityIcons name="alert-circle-outline" size={18} color={palette.fg} />
      <Text style={[styles.text, { color: palette.fg }]}>
        Your last payment failed. Update your billing to keep your subscription active.
      </Text>
      <Pressable
        onPress={handleUpdateBilling}
        style={[styles.cta, { borderColor: palette.fg }]}
        accessibilityRole="button"
        accessibilityLabel="Update billing"
      >
        <Text style={[styles.ctaText, { color: palette.fg }]}>Update billing</Text>
      </Pressable>
    </View>
  );
};

const AMBER_PALETTE = { bg: '#FEF3C7', border: '#FDE68A', fg: '#92400E' };
const RED_PALETTE = { bg: '#FEE2E2', border: '#FCA5A5', fg: '#991B1B' };

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    // 'sticky' is web-only; React Native's StyleSheet type only allows
    // 'absolute' | 'relative'. Gating on Platform.OS keeps the banner
    // viable on RN-web (current target) without emitting an invalid
    // style key on iOS/Android.
    ...(Platform.OS === 'web' ? { position: 'sticky', top: 0 } : {}),
    zIndex: 50,
  },
  text: {
    flex: 1,
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    lineHeight: 18,
  },
  cta: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 6,
  },
  ctaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
  },
});

export default PastDueBanner;
